import { ConnectedGallery, ConnectionsService, TermsAcceptanceStatus } from '../../../Services/connections.service';
import { Consignment, ConsignmentProposalRequest, ProposalHistoryResponse } from '../../../Services/consignment.service';
import { saveAs } from 'file-saver';
import { GalleriesService, GalleryItems } from '../../../Services/galleries.service';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationsService } from '../../../Services/shared/notifications.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { SelectItem, ConfirmationService, MenuItem } from 'primeng/api';
import { Artist, ArtistsService } from '../../../Services/artists.service';
import { AuthConfig, AuthService, ResponseObject } from '../../../Services/auth.service';
import { ImageUrls, NotificationTypes, UserRoles } from '../../../app.enums';
import { ArtistProductsService } from '../../../Services/artistproducts.service';
import { ConsignmentService } from '../../../Services/consignment.service';
import { ArtistProduct } from 'src/app/models/artistproduct.model';
import { MessagingService } from 'src/app/Services/messaging.service';
import { ImageUtils } from 'src/app/shared/utils/image-utils';
import { ProposalService } from 'src/app/Services/ProposalService/ProposalService.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'view-consignment',
    templateUrl: './view-consignment.v2.component.html',
    styleUrls: ['./view-consignment.component.css'],
    providers: [GalleriesService, ArtistProductsService, ConnectionsService, ConsignmentService],
    standalone: false
})
export class ViewConsignmentComponent implements OnInit {
  // Main consignment object to hold current consignment data
  public consignment!: Consignment;
  // Boolean flags for proposal status tracking
  public isProposalAccepted!: boolean;
  public isProposalPending!: boolean;
  // Current artist product being processed
  public ArtistProduct!: ArtistProduct
  // Product availability flag
  public isProductAvailable: boolean = true;
  // Flag to check if consignment data is ready for display
  public isConsignmentReady: boolean = false;
  // Array defining consignment status options
  public statusItem = ['Pending', 'Catalogue', 'Delivery', 'Sales'];
  // Flag to track if current inventory has been loaded
  public isCurrentInventoryLoaded: boolean = false;
  // Arrays for managing product inventory
  public currentInventory: ArtistProduct[] = [];
  public selectedInventory: ArtistProduct[] = [];
  // Dropdown data arrays
  public artistConnectedGalleries: SelectItem[] = [];
  public artists: SelectItem[] = [];
  // Authentication configuration object
  public authConfig: AuthConfig = {};
  // ID properties for routing and data fetching
  public artistId!: string;
  public consignmentId!: string;
  // Currently selected product for operations
  public selectedProduct!: ArtistProduct;
  // Commission percentage options for dropdown
  public commissionOptions: { value: number; label: string }[] = [];
  // Dialog visibility flags
  public isArtistRevenuDailogVisible: boolean = false;
  public showPriceCalculatorDialog: boolean = false;
  public showCommentDialog: boolean = false;
  public showCommentProposalDialog: boolean = false;
  public showProposalHistory: boolean = false;
  // Form data objects for various operations
  public formData!: ConsignmentCommentRequest;
  public consignmentProposalData: ConsignmentProposalRequest = {} as ConsignmentProposalRequest;
  // Proposal history data array
  public proposalHistory: ProposalHistoryResponse[] = [];
  // Breadcrumb navigation items
  public items: MenuItem[] | undefined;
  public home: MenuItem | undefined;
  // Terms acceptance related properties
  public showTermsAcceptanceDialog: boolean = false;
  public termsAcceptanceStatus!: TermsAcceptanceStatus;
  // Toggle switch state
  public checked: boolean = false;
  // Sorting properties for inventory list
  public isSortedByAlphabet: boolean = false;
  public sortLabel: string = 'Sort Alphabetically';
  // Properties for product details dialog
  public selectedItem: any; // Currently selected item
  public artistGlazes: any[] = []; // Artist glazes array
  public isProductDetailsDialogVisible: boolean = false; // Product details dialog visibility flag
  // Properties for product dialog
  public showProductDialog = false;
  public productId: string | null = null;
  // Handle toggle mode change

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private ngxSpinnerService: NgxSpinnerService,
    private confirmationService: ConfirmationService,
    private notificationsService: NotificationsService,
    private galleriesService: GalleriesService,
    private artistService: ArtistsService,
    private authService: AuthService,
    private messagingService: MessagingService,
    private connectionsService: ConnectionsService,
    private ArtistProductsService: ArtistProductsService,
    private consignmentService: ConsignmentService,
    private ProposalService: ProposalService,
    private transloco: TranslocoService
  ) {
    // Initialize form data object
    this.formData = new ConsignmentCommentRequest();
  }

  public ngOnInit(): void {
    this.authConfig = this.authService.getAuthConfig();
    // Subscribe to route parameters to get artistId and consignmentId
    this.activatedRoute.params.subscribe((params) => {
      if (params['artistId']) {
        // Set artistId for admin users, null for others
        this.artistId = this.authConfig.IsSuperAdmin || this.authConfig.IsAdministrator ? params['artistId'] : null;
      }

      if (params['consignmentId']) {
        // If editing existing consignment, get consignment data
        this.consignmentId = params['consignmentId'];
        this.getConsignmentById(this.consignmentId);
      } else {
        // If creating new consignment, initialize with default values
        this.consignment = new Consignment();
        this.consignment.ConsignmentStatus = 'Delivery'; // Default status
        this.consignment.GallerySaleType = 'Consignment'; // Default sale type
        this.isConsignmentReady = true;
      }
    });

    // Set artistId from token if user is an artist
    if (this.authConfig.IsArtist) {
      this.artistId = this.authService.getUserId();
    }

    // Initialize data only if we have required information
    if (!this.consignmentId && this.authConfig.IsSuperAdmin || this.authConfig.IsAdministrator) {
      // Don't initialize for admin users creating new consignment without artist selection
    } else {
      // Get galleries connected to this artist
      this.getAllGalleries(this.artistId);
      // Initialize inventory lists for this artist
      this.initalizeInventoryLists(this.artistId, true);
    }

    // Load artists dropdown for admin users
    if (this.authConfig.IsSuperAdmin || this.authConfig.IsAdministrator) {
      this.getAllArtists();
    }

    // Generate commission percentage options from 5% to 90%
    for (let i = 5; i <= 90; i += 5) {
      this.commissionOptions.push({ value: i, label: `${i}%` });
    }
  }

  // Handle real-time notifications from messaging service
  public reciveNotifications() {
    this.messagingService.currentMessage.subscribe((data: any) => {
      this.messagingService.handleNotificationRead(data.data.entityId);
    });
  }

  // Fetch all galleries connected to the specified artist
  private getAllGalleries(artistId: string) {
    this.ngxSpinnerService.show();
    this.connectionsService.getConnectedGalleries(artistId, false).subscribe((galleries: ConnectedGallery[]) => {
      // Map galleries to dropdown format
      this.artistConnectedGalleries = galleries.map((gallery) => ({ label: gallery.Name, value: gallery.Id }));
      this.ngxSpinnerService.hide();
    }, (error) => {
      console.error(error);
      this.ngxSpinnerService.hide();
    });
  }

  // Store selected gallery details
  public selectedGalleryDetails: GalleryItems | null = null;

  // Handle gallery selection change event
  public onGalleryChanged($event: any) {
    const galleryId = $event.value;
    this.getGalleryById(galleryId, (gallery: GalleryItems) => {
      if (gallery) {
        // Reset sale type to default and update commission from gallery
        this.consignment.GallerySaleType = 'Consignment';
        this.consignment.Commission = gallery.CommissionPercentage;
      }
    });
  }

  // Fetch gallery details by ID and execute callback
  public getGalleryById(galleryId: string, callback: Function) {
    this.ngxSpinnerService.show();
    this.galleriesService.fetchById(galleryId).subscribe((gallery) => {
      this.selectedGalleryDetails = gallery;
      // Set sales tax percentage from gallery data
      this.consignment.SalesTaxPercentage = gallery.SalesTaxPercentage ?? 0;
      this.ngxSpinnerService.hide();
      callback(gallery);
    }, (error) => {
      console.error(error);
      this.ngxSpinnerService.hide();
    });
  }

  // Handle inventory item selection
  public selectInventory(event: ArtistProduct) {
    // Check if item is not already selected
    if (!this.selectedInventory.some(item => item.Id === event.Id)) {
      // Check if product has artist revenue set
      if (event.ArtistRevenue === null) {
        // Show dialog to set artist revenue
        console.log(event, ':  productsaavalable');
        this.selectedProduct = event
        this.isArtistRevenuDailogVisible = true;
        return;
      }

      // Move product to selected inventory
      this.selectedInventory.push(event);

      // Remove from current inventory
      const index = this.currentInventory.findIndex(item => item.Id === event.Id);
      if (index !== -1) {
        this.currentInventory.splice(index, 1);
      }

      // Update consignment selected products
      this.consignment.SelectedProductIds = this.selectedInventory.map(p => p.Id);
    }
    console.log("Selected Inventory", this.selectedInventory);
  }

  // Move item from selected back to current inventory
  public moveToCurrentInventory(selectedInventory: ArtistProduct) {
    // Find and remove from selected inventory
    const currentIndex = this.selectedInventory.findIndex(item => item.Id === selectedInventory.Id);

    if (currentIndex !== -1) {
      const removedItem = this.selectedInventory.splice(currentIndex, 1)[0];
      this.currentInventory.push(removedItem);
    }
  }

  // Initialize inventory lists for the specified artist
  private initalizeInventoryLists(artistId: string, isInitialCall: boolean) {
    this.isCurrentInventoryLoaded = false;
    this.currentInventory = [];
    this.selectedInventory = [];

    // Get all products for this artist
    this.getArtistProducts(artistId, (artistProducts: ArtistProduct[]) => {
      this.currentInventory = artistProducts;
      this.isCurrentInventoryLoaded = true;

      if (isInitialCall && this.consignmentId) {
        // Load already selected products for existing consignment
        this.getConsignmentProducts(this.consignmentId, (consignmentProducts: ArtistProduct[]) => {
          this.selectedInventory = consignmentProducts;
          // Remove selected products from current inventory
          this.currentInventory = this.currentInventory.filter(ciItem => !this.selectedInventory.some(slitem => slitem.Id === ciItem.Id));
        });
      }
    });
  }

  // Fetch all products for the specified artist
  public getArtistProducts(artistId: string, callback: Function) {
    this.ArtistProductsService.getArtistProducts(artistId, true).subscribe((artistProducts) => {
      // Process product images and execute callback
      artistProducts.forEach((product: ArtistProduct) => {
        product.ProductImgUrl = ImageUtils.getResizedCloudinaryUrl(product.ProductImgUrl || 'assets/images/No-Image.png');
      });
      callback(artistProducts);
    }, (error) => {
      console.error(error);
      callback([]);
    });
  }

  // Fetch products already assigned to a consignment
  public getConsignmentProducts(consignmentId: string, callback: Function) {
    this.consignmentService.getConsignmentProducts(consignmentId).subscribe((consignmentProducts) => {
      callback(consignmentProducts);
    }, (error) => {
      console.error(error);
      callback([]);
    });
  }

  // Update artist revenue for a product
  public updateArtistRevenue(selectedProduct: ArtistProduct) {
    this.ngxSpinnerService.show();
    this.ArtistProductsService.updateArtistProductRevenue(selectedProduct.Id, selectedProduct.ArtistRevenue).subscribe((response: ResponseObject) => {
      this.ngxSpinnerService.hide();
      if (response.Success) {
        this.isArtistRevenuDailogVisible = false;
        this.selectInventory(selectedProduct);
        this.notificationsService.showNotification(this.transloco.translate('viewConsignment.success'), this.transloco.translate('viewConsignment.changesSaved'), NotificationTypes.Success);
      } else {
        this.notificationsService.showNotification(this.transloco.translate('viewConsignment.error'), this.transloco.translate('viewConsignment.savingConsignmentError'), NotificationTypes.Error);
      }
    }, (error) => {
      console.error(error);
      this.ngxSpinnerService.hide();
      this.notificationsService.showNotification(this.transloco.translate('viewConsignment.error'), error, NotificationTypes.Error);
    });
  }

  // Navigate to edit product page
  public onEditSelectedProduct(productId: string) {
    this.router.navigate(['/artists', 'artworks', productId]);
  }

  // Close artist revenue dialog and reset data
  public closeDialog() {
    this.isArtistRevenuDailogVisible = false;
    this.selectedProduct.ArtistRevenue = null!;
  }

  // Close dispatch comment dialog and reset form data
  public closeDispatchDialog() {
    this.showCommentDialog = false;
    this.formData = new ConsignmentCommentRequest();
  }

  // Close proposal dialog and reset proposal data
  public closeProposalDialog() {
    this.showCommentProposalDialog = false;
    this.consignmentProposalData = new ConsignmentProposalRequest();
  }

  // Inject calculated artist revenue from price calculator
  public onInjectArtistRevenue($calculatedArtistRevenue: number) {
    if (this.selectedProduct) {
      this.selectedProduct.ArtistRevenue = $calculatedArtistRevenue;
      this.onPriceCalculatorDialogClose({});
    }
  }

  // Open price calculator dialog
  public openPriceCalculatorDialog($event: any) {
    this.showPriceCalculatorDialog = true;
  }

  // Close price calculator dialog
  public onPriceCalculatorDialogClose($event: any) {
    this.showPriceCalculatorDialog = false;
  }

  // Close proposal history dialog
  public onShowProposalHistoryDialogClose($event: any) {
    this.showProposalHistory = false;
  }

  // Fetch all artists for admin dropdown
  private getAllArtists() {
    this.artistService.getArtistsDropdownData().subscribe((artists) => {
      this.artists = artists;
    }, (error) => {
      console.error(error);
    });
  }

  // Save consignment (create new or update existing)
  public saveConsignment($event: any) {
    // Assign selected product IDs to consignment
    this.consignment.SelectedProductIds = this.selectedInventory.map(p => p.Id);

    if (this.authConfig.IsArtist) {
      // Set artist ID from token for artist users
      this.consignment.ArtistId = this.authService.getUserId();
    } else if (this.authConfig.IsSuperAdmin || this.authConfig.IsAdministrator) {
      this.consignment.ArtistId = this.artistId;
    }

    if (!this.consignment.Guid) {
      // Create new consignment
      this.ngxSpinnerService.show();
      this.consignmentService.createConsignment(this.artistId, this.consignment.GalleryId, {
        Name: this.consignment.Name,
        ConsignmentStatus: this.consignment.ConsignmentStatus,
        GallerySaleType: this.consignment.GallerySaleType,
        SalesTaxPercentage: this.consignment.SalesTaxPercentage,
        Commission: this.consignment.Commission,
        SelectedProductIds: this.consignment.SelectedProductIds
      }).subscribe((response) => {
        this.ngxSpinnerService.hide();
        if (response.Success && response.ResponseData) {
          var consignmentId = response.ResponseData;
          // Navigate based on user role
          if (this.authConfig.IsSuperAdmin || this.authConfig.IsAdministrator) {
            this.router.navigate(['artists', this.consignment.ArtistId, 'consignments', consignmentId, 'view'])
          }
          if (this.authConfig.IsArtist) {
            this.router.navigate(['artists', 'consignments', consignmentId, 'view']);
          }
          this.notificationsService.showNotification(this.transloco.translate('viewConsignment.success'), this.transloco.translate('viewConsignment.consignmentCreated'), NotificationTypes.Success);
        } else {
          console.error(response);
          this.notificationsService.showNotification(this.transloco.translate('viewConsignment.error'), this.transloco.translate('viewConsignment.savingConsignmentError'), NotificationTypes.Error);
        }
      }, (error) => {
        console.error(error);
        this.ngxSpinnerService.hide();
      });
    } else {
      // Update existing consignment
      this.updateConsignment((response: ResponseObject) => {
        if (response.Success) {
          this.notificationsService.showNotification(this.transloco.translate('viewConsignment.success'), this.transloco.translate('viewConsignment.consignmentUpdated'), NotificationTypes.Success);
        } else {
          console.error(response);
          this.notificationsService.showNotification(this.transloco.translate('viewConsignment.error'), this.transloco.translate('viewConsignment.savingConsignmentError'), NotificationTypes.Error);
        }
      });
    }
  }

  // Update existing consignment with current data
  private updateConsignment(callback: Function) {
    this.consignment.SelectedProductIds = this.selectedInventory.map(p => p.Id);
    this.ngxSpinnerService.show();
    this.consignmentService.updateConsignment(this.consignment.Guid, {
      Name: this.consignment.Name,
      ConsignmentStatus: this.consignment.ConsignmentStatus,
      GallerySaleType: this.consignment.GallerySaleType,
      SalesTaxPercentage: this.consignment.SalesTaxPercentage,
      Commission: this.consignment.Commission,
      SelectedProductIds: this.consignment.SelectedProductIds
    }).subscribe((response) => {
      this.ngxSpinnerService.hide();
      callback(response);
    }, (error) => {
      console.error(error);
      this.ngxSpinnerService.hide();
    });
  }

  // Handle artist selection change for admin users
  public onArtistChanged($selectedArtistId: string) {
    this.artistId = $selectedArtistId;
    // Reinitialize data for new artist
    this.initalizeInventoryLists(this.artistId, false);
    this.getAllGalleries(this.artistId)
  }

  // Fetch consignment details by ID
  private getConsignmentById(consignmentId: string) {
    this.ngxSpinnerService.show();
    this.consignmentService.getConsignmentById(consignmentId).subscribe((consignment) => {
      this.consignment = consignment;
      // Set up breadcrumb navigation
      this.items = [{ label: 'Consignments', routerLink: ['/view-consignments'] }, { label: this.consignment.Name }];
      this.home = { icon: 'pi pi-home', routerLink: '/' };
      this.isConsignmentReady = true;
      this.ngxSpinnerService.hide();
      console.log(this.consignment);

      if (this.consignment && this.consignment.GalleryId) {
        // Get gallery details for this consignment
        this.getGalleryById(this.consignment.GalleryId, (gallery: GalleryItems) => {
          this.consignment.Gallery = gallery;
        });
      }
    }, (error) => {
      console.error(error);
      this.ngxSpinnerService.hide();
    });
  }

  // Clear consignment selection and reset to new consignment
  public clearSelection($event: any) {
    this.consignment = new Consignment();
  }

  // Open proposal dialog with consignment data
  public sendConsignmentProposalDialog(consignment: Consignment) {
    this.showCommentProposalDialog = true;
    this.consignmentProposalData.ArtistId = this.artistId;
    this.consignmentProposalData.GalleryId = consignment.GalleryId;
    this.consignmentProposalData.ConsignmentId = consignment.Guid;
  }

  // Open dispatch dialog after checking terms acceptance
  public dispatchConsignmentDialog() {
    // Check terms acceptance first, then show comment dialog
    this.checkTermsAcceptanceBeforeComment();
  }

  // Check if gallery terms are accepted before proceeding
  private checkTermsAcceptanceBeforeComment(): void {
    if (!this.consignment.GalleryId) {
      this.notificationsService.showNotification(
        this.transloco.translate('viewConsignment.error'),
        this.transloco.translate('viewConsignment.galleryNotSelected'),
        NotificationTypes.Error
      );
      return;
    }

    this.ngxSpinnerService.show();
    this.connectionsService.termsAcceptance(this.consignment.GalleryId).subscribe(
      (response: ResponseObject) => {
        this.ngxSpinnerService.hide();
        if (response.Success) {
          this.termsAcceptanceStatus = response.ResponseData;

          // Check if terms are accepted
          if (this.termsAcceptanceStatus.IsAccepted) {
            // Terms are accepted, show comment dialog directly
            this.showCommentDialog = true;
          } else {
            // Terms not accepted, show terms dialog first
            this.showTermsAcceptanceDialog = true;
          }
        } else if (!response.Success && response.ResponseMessage === "Gallery terms not found.") {
          // No terms exist, skip terms dialog and show comment dialog directly
          this.showCommentDialog = true;
        } else {
          this.notificationsService.showNotification(
            this.transloco.translate('viewConsignment.error'),
            this.transloco.translate('viewConsignment.checkTermsStatusFailed'),
            NotificationTypes.Error
          );
        }
      },
      (error) => {
        console.error('Error checking terms acceptance:', error);
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(
          this.transloco.translate('viewConsignment.error'),
          this.transloco.translate('viewConsignment.checkTermsError'),
          NotificationTypes.Error
        );
      }
    );
  }

  // Handle terms acceptance completion
  public onTermsAccepted(): void {
    this.showTermsAcceptanceDialog = false;
    // After terms are accepted, show the comment dialog
    this.showCommentDialog = true;
  }

  // Handle terms acceptance cancellation
  public onTermsCancelled(): void {
    this.showTermsAcceptanceDialog = false;
  }

  // Dispatch consignment to gallery after confirmation
  public dispatchConsignment($event: any) {
    // Populate form data with selected product IDs
    this.formData.ConsignmentProductIds = this.selectedInventory.map((product) => product.Id);

    // Validate form data before API call
    if (!this.validateFormData(this.formData)) {
      this.notificationsService.showNotification(
        this.transloco.translate('viewConsignment.error'),
        this.transloco.translate('viewConsignment.noProductsSelected'),
        NotificationTypes.Error
      );
      return;
    }

    this.confirmationService.confirm({
      message: this.transloco.translate('viewConsignment.dispatchConfirm', { galleryName: this.consignment.Gallery?.Name }),
      accept: () => {
        // First update the current state of this consignment
        this.updateConsignment((response: ResponseObject) => {
          if (response.Success) {
            // Log dispatch data for debugging
            console.log('Dispatching consignment with the following data:', {
              consignmentId: this.consignmentId,
              formData: this.formData,
            });

            // Dispatch the consignment
            this.ngxSpinnerService.show();
            this.consignmentService
              .dispatchConsignment(this.consignmentId, this.formData)
              .subscribe(
                (response: ResponseObject) => {
                  this.ngxSpinnerService.hide();
                  if (response.Success) {
                    this.consignment.IsDispatched = true;
                    this.showCommentDialog = false;
                    this.formData = new ConsignmentCommentRequest();
                    this.notificationsService.showNotification(
                      this.transloco.translate('viewConsignment.success'),
                      this.transloco.translate('viewConsignment.consignmentDispatched'),
                      NotificationTypes.Success
                    );
                    // Refresh consignment data to update details
                    this.getConsignmentById(this.consignmentId);
                  } else {
                    this.notificationsService.showNotification(
                      this.transloco.translate('viewConsignment.error'),
                      this.transloco.translate('viewConsignment.dispatchErrorDetails', { details: response.ResponseMessage }),
                      NotificationTypes.Error
                    );
                  }
                },
                (error) => {
                  console.error('API Error:', error);
                  this.ngxSpinnerService.hide();
                  this.notificationsService.showNotification(
                    this.transloco.translate('viewConsignment.error'),
                    this.transloco.translate('viewConsignment.dispatchUnexpectedError'),
                    NotificationTypes.Error
                  );
                }
              );
          } else {
            this.notificationsService.showNotification(
              this.transloco.translate('viewConsignment.error'),
              this.transloco.translate('viewConsignment.savingConsignmentError'),
              NotificationTypes.Error
            );
          }
        });
      },
    });
  }

  // Validate form data before submission
  private validateFormData(formData: ConsignmentCommentRequest): boolean {
    // Ensure ConsignmentProductIds is not empty
    if (!formData.ConsignmentProductIds || formData.ConsignmentProductIds.length === 0) {
      return false;
    }
    return true;
  }

  // Emoji dialog visibility flag
  public displayEmojiDialog: boolean = false

  // Toggle emoji dialog visibility
  public openEmojiDialog() {
    this.displayEmojiDialog = !this.displayEmojiDialog;
  }

  // Add selected emoji to comment
  public addEmoji(emoji: string) {
    console.log('Selected emoji:', emoji);
    this.formData.ConsignmentDispatchComment = (this.formData.ConsignmentDispatchComment || '') + emoji;
    this.displayEmojiDialog = false;
  }

  // Close emoji selection dialog
  public closeEmojiDialog($event: any) {
    this.displayEmojiDialog = false;
  }

  // Generate and download PDF for consignment
  public generatePdf($event: any) {
    this.ngxSpinnerService.show();
    this.consignmentService.generatePDF(this.consignmentId).subscribe((response: Blob) => {
      var fileName = this.consignment?.Name + '-' + (new Date().getTime()) + '.pdf';
      // Save the generated PDF file
      saveAs(new Blob([response], { type: 'application/pdf' }), fileName);
      this.ngxSpinnerService.hide();
    }, (error) => {
      console.error(error);
      this.ngxSpinnerService.hide();
    });
  }

  // Send consignment proposal to gallery
  public sendConsignmentProposal() {
    this.consignmentProposalData.SelectedProductIds = this.selectedInventory.map(p => p.Id);
    this.ngxSpinnerService.show();
    this.ProposalService.sendConsignmentProposal(this.consignmentProposalData).subscribe((response: ResponseObject) => {
      this.ngxSpinnerService.hide();
      if (response.Success) {
        this.showCommentProposalDialog = false;
        this.consignmentProposalData = new ConsignmentProposalRequest();
        this.notificationsService.showNotification(this.transloco.translate('viewConsignment.success'), this.transloco.translate('viewConsignment.proposalSent'), NotificationTypes.Success);
      } else {
        this.notificationsService.showNotification(this.transloco.translate('viewConsignment.error'), this.transloco.translate('viewConsignment.proposalErrorDetails', { details: response.ResponseMessage }), NotificationTypes.Error);
      }
    }, (error) => {
      console.error(error);
      this.ngxSpinnerService.hide();
    });
  }

  // Check if given date is today
  isToday(date: Date): boolean {
    const today = new Date();
    const providedDate = new Date(date);
    return providedDate.getFullYear() === today.getFullYear() &&
      providedDate.getMonth() === today.getMonth() &&
      providedDate.getDate() === today.getDate();
  }

  // Calculate how many days ago the consignment was dispatched
  dispatchedDaysAgo(date: Date): number {
    const currentDate = new Date();
    const dispatchedDate = new Date(date);
    const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
    const daysAgo = Math.round(Math.abs((currentDate.getTime() - dispatchedDate.getTime()) / oneDay));
    return daysAgo;
  }

  // Open proposal history dialog
  public proposalHistoryDialog() {
    this.showProposalHistory = true;
  }



  // Prevent negative values in artist revenue input
  public preventNegativeValues(event: any): void {
    let value = event.target.value;
    value = value.replace(/-,+/g, '');
    // Allow only valid decimal numbers (positive values with an optional decimal point)
    const regex = /^[0-9]*\.?[0-9]*$/;

    if (!regex.test(value)) {
      // If invalid, revert to previous valid value or set to empty string
      this.selectedProduct.ArtistRevenue = 0;
    } else {
      // Ensure value remains a valid positive decimal number
      this.selectedProduct.ArtistRevenue = value;
    }
    if (event.target.value < 0) {
      this.selectedProduct.ArtistRevenue = 0
    }
  }


  // Toggle between alphabetical and date sorting for inventory
  public toggleSort(): void {
    if (!this.isSortedByAlphabet) {
      // Sort alphabetically (A-Z)
      this.currentInventory.sort((a, b) =>
        a.Name.localeCompare(b.Name)
      );
      this.sortLabel = 'Sort by Date';
    } else {
      // Sort by date (Newest First)
      this.currentInventory.sort((a, b) => {
        const dateA = new Date(a.CreatedDate).getTime();
        const dateB = new Date(b.CreatedDate).getTime();
        return dateB - dateA;
      });
      this.sortLabel = 'Sort by Alphabet';
    }

    this.isSortedByAlphabet = !this.isSortedByAlphabet;
  }


  // Show product details dialog for selected product
  public showProductDetailsDialog(artistProductId: string): void {
    this.ngxSpinnerService.show();
    this.ArtistProductsService.getProductDetail(artistProductId).subscribe((artistProduct: ArtistProduct) => {
      this.selectedProduct = artistProduct;
      console.log(this.selectedProduct)
      this.isProductDetailsDialogVisible = true;
      this.ngxSpinnerService.hide();
    }, (error) => {
      console.error(error);
      this.ngxSpinnerService.hide();
    });
  }

  // Close product details dialog
  public closeProductDetailsDialog(): void {
    this.isProductDetailsDialogVisible = false;
  }



  // Open product dialog with specified product ID
  openProductDialog(productId: string) {
    this.productId = productId;
    this.showProductDialog = true;
  }

  // Close product dialog and reset product ID
  closeProductDialog() {
    this.showProductDialog = false;
    this.productId = null;
  }
  toggleMode() {
    console.log('Checked state:', this.checked);
  }
}

// Class for consignment comment request data
export class ConsignmentCommentRequest {
  ConsignmentDispatchComment?: string;
  ConsignmentAcceptComment?: string;
  ConsignmentProductIds: string[] = [];
  ProductSyncRequests: ProductSyncRequest[] = [];
  isUserAddManullyRates!: boolean
}

// Class for product sync request data
export class ProductSyncRequest {
  ConsignmentProductId?: string;
  IsSyncToShopify!: boolean;
  IsSyncToSquareUp!: boolean
  IsSyncToWooCommerce!: boolean
  IsSyncToWix!: boolean;
  IsSyncToSquareSpace!: boolean;
}

// Class for sync accepted product data
export class SyncAcceptedProduct {
  isUserAddManullyRates!: boolean;
  woocommerceAccountId?: string;
  shopifyAccountId?: string;
  SquareSpaceId?: string;
  squareSpaceAccountInfo?: SquareSpaceAccountInfo;
  productSyncRequests!: ProductSyncRequest[];
}

// Class for SquareSpace account information
export class SquareSpaceAccountInfo {
  id?: string;
  pageId?: string
}